import React from "react";
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from "@heroicons/react/24/solid";

const DevisDocument = ({
  patientName,
  date,
  items,
  discountAmount = 0,
  totalAfterDiscount,
  currentTeeth = [],
}) => {
  const subtotal = items.reduce((sum, item) => {
    const price =
      item.price === "" || item.price == null ? 0 : Number(item.price);
    if (Number.isNaN(price)) return sum;
    return sum + price;
  }, 0);

  return (
    <div className="bg-white print-area flex flex-col min-h-screen">
      <style>{`
        @page {
          size: A4;
          margin: 2.5cm;
        }

        /* ---------- PRINT FIX ONLY (no visual change) ---------- */
@media print {
  body, html {
    margin: 0;
    padding: 0;
    background: white;
  }

  /* TRUE A4 PAGE */
  .print-area {
    position: relative;
    width: 21cm;
    height: 29.7cm;          /* fixed physical page */
    padding: 2.5cm;
    box-sizing: border-box;
    overflow: hidden;        /* prevents page spill */
    font-size: 11pt;
    line-height: 1.4;
  }
.print-footer {
    position: absolute;
    bottom: 2.5cm;       /* same as page margin */
    left: 0;             /* full width */
    right: 0;
    width: 100%;
}

.print-footer::before {
    content: "";
    display: block;
    border-top: 2px solid #d1d5db; /* thick gray line like your header */
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

.print-footer .footer-row {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: nowrap;
    padding-top: 6px;  /* space between line and content */
}

  table {
    width: 100%;
    border-collapse: collapse;
  }

  tr, td, th {
    page-break-inside: avoid;
  }
}

      `}</style>
      <div className="print-content">
        {/* Header */}
        <div className="grid grid-cols-3 gap-1 items-start mb-2 pb-3 border-b-2 border-gray-300">
          <div className="flex flex-col justify-start text-center">
            <div className="text-blue-600 font-bold text-sm leading-tight">
              Cabinet Dentaire
            </div>
            <div className="text-green-600 font-semibold text-xs leading-tight">
              Dr. YOUSSEF LACHHAB
            </div>
            <div className="text-xs text-gray-700 leading-tight mb-1.5">
              Chirurgien Dentiste
            </div>
            <div className="text-xs text-gray-600 leading-tight">
              ORTHODONTIE – RADIOLOGIE – IMPLANTOLOGIE – SOINS DENTAIRE –
              BLANCHIMENT – CHIRURGIE – PROTHÈSE – ESTHÉTIQUE DU SOURIRE
            </div>
          </div>

          <div className="flex justify-center items-start -mt-9">
            <img
              src="/images/logo.svg"
              alt="Logo"
              className="h-40 w-40 object-contain"
            />
          </div>

          <div className="flex flex-col justify-start text-center">
            <div className="text-blue-600 font-bold text-sm leading-tight">
              عيادة طب الأسنان
            </div>
            <div className="text-green-600 font-semibold text-xs leading-tight">
              الدكتور يوسف الشهب
            </div>
            <div className="text-xs text-gray-700 leading-tight mb-1.5">
              طبيب جراحة الأسنان
            </div>
            <div className="text-xs text-gray-600 leading-tight" dir="rtl">
              تقويم الأسنان – الأشعة – زراعة الأسنان – علاج الأسنان – التبييض –
              الجراحة – تركيب وتعويض الأسنان – تجميل الابتسامة
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-black text-center text-gray-900 mb-4 mt-2 leading-tight tracking-wide">
          DEVIS
        </h1>

        {/* Patient info */}
        <div className="flex justify-between items-start mb-2 pb-2 border-b border-gray-200 gap-4">
          <div className="flex-1">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5">
              Nom et prénom
            </div>
            <div className="text-base font-semibold text-gray-900">
              {patientName || "________________________"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5">
              Date d'émission
            </div>
            <div className="text-base font-semibold text-gray-900">
              {date || "__________"}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-xs border border-gray-300 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-blue-50">
                <th className="px-2 py-2 text-left font-semibold text-gray-800">
                  Dents
                </th>
                <th className="px-2 py-2 text-left font-semibold text-gray-800">
                  Acte
                </th>
                <th className="px-2 py-2 text-right font-semibold text-gray-800">
                  Prix
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                if (!item.soinCode && !item.toothCode) return null;
                return (
                  <tr key={item.id} className="border-t border-gray-200">
                    <td className="px-2 py-2 font-medium">
                      {item.toothCode || "-"}
                    </td>
                    <td className="px-2 py-2">{item.soinName || "-"}</td>
                    <td className="px-2 py-2 text-right font-medium">
                      {item.price !== "" && item.price != null
                        ? `${Number(item.price).toFixed(2)} DH`
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-4 mb-4 flex justify-end">
          <div className="w-full sm:w-72 space-y-1">
            {discountAmount > 0 && (
              <>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Sous-total</span>
                  <span>{subtotal.toFixed(2)} DH</span>
                </div>
                <div className="flex justify-between text-xs text-green-600">
                  <span>Remise</span>
                  <span>- {discountAmount.toFixed(2)} DH</span>
                </div>
              </>
            )}

            <div className="border-t-2 border-gray-300 pt-1 flex justify-between items-center bg-blue-50 px-3 py-2 rounded-lg">
              <span className="text-base font-bold text-gray-900">
                TOTAL TTC :
              </span>
              <span className="text-base font-bold text-blue-600">
                {totalAfterDiscount.toFixed(2)} DH
              </span>
            </div>
          </div>
        </div>

        {/* Signature */}
        <div className="flex justify-start pt-3 border-t border-gray-300">
          <div>
            <div className="text-xs text-gray-700 mb-2 italic">
              Lu, compris et approuvé, avec possibilité d'évolution selon les
              nécessités cliniques
            </div>
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
              Signature du patient
            </div>
            <div className="border-b-2 border-gray-400 w-52" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="print-footer text-xs text-gray-600">
        <div className="flex flex-col items-center space-y-1 leading-tight">
          <div className="footer-row flex items-center justify-center text-center">
            <MapPinIcon className="w-3 h-3 text-gray-500" />
            <span>
              Lot Khiam 06, Avenue Moulay Rachid, N°22, 1er étage, Hay Al
              Inbiat, Temara
            </span>
          </div>

          <div className="footer-row flex items-center justify-center space-x-4">
            <div className="flex items-center space-x-1">
              <PhoneIcon className="w-3 h-3 text-gray-500" />
              <span>07 08 44 44 54</span>
            </div>
            <div className="flex items-center space-x-1">
              <PhoneIcon className="w-3 h-3 text-gray-500" />
              <span>05 38 44 44 45</span>
            </div>
            <div className="flex items-center space-x-1">
              <EnvelopeIcon className="w-3 h-3 text-gray-500" />
              <span>drlachhab.youssef@gmail.com</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevisDocument;
